import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { showError, showSuccess } from '../utils/toast';

const ExamResults = () => {
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedStudents, setExpandedStudents] = useState(new Set());
  const [examSortBy, setExamSortBy] = useState('submissions'); // 'submissions', 'dateAsc', 'dateDesc', 'nameAsc'
  const [studentSortBy, setStudentSortBy] = useState('score'); // 'score', 'dateAsc', 'dateDesc', 'nameAsc'

  useEffect(() => {
    fetchExamsWithSubmissions();
  }, []);

  const fetchExamsWithSubmissions = async () => {
    try {
      setLoading(true);
      const adminId = localStorage.getItem('adminId');
      
      // Fetch all exams created by this organization
      const examsQuery = query(
        collection(db, 'exams'),
        where('organizationId', '==', adminId)
      );
      const examsSnapshot = await getDocs(examsQuery);
      
      const examsList = [];
      for (const examDoc of examsSnapshot.docs) {
        const examData = { id: examDoc.id, ...examDoc.data() };
        
        // Get submission count for this exam
        const submissionsQuery = query(
          collection(db, 'examSubmissions'),
          where('examId', '==', examDoc.id),
          where('status', '==', 'completed')
        );
        const submissionsSnapshot = await getDocs(submissionsQuery);
        
        examData.submissionCount = submissionsSnapshot.size;
        examsList.push(examData);
      }
      
      setExams(examsList);
      
    } catch (error) {
      console.error('Error fetching exams:', error);
      showError('Failed to load exams');
    } finally {
      setLoading(false);
    }
  };

  const fetchExamSubmissions = async (examId) => {
    try {
      setLoading(true);
      
      // Fetch all submissions for this exam
      const submissionsQuery = query(
        collection(db, 'examSubmissions'),
        where('examId', '==', examId),
        where('status', '==', 'completed')
      );
      const submissionsSnapshot = await getDocs(submissionsQuery);
      
      const submissionsList = [];
      submissionsSnapshot.forEach((doc) => {
        const data = { id: doc.id, ...doc.data() };
        submissionsList.push(data);
      });
      
      // Group submissions by student and keep their attempts
      const studentSubmissions = {};
      submissionsList.forEach(submission => {
        const studentId = submission.studentId;
        if (!studentSubmissions[studentId]) {
          studentSubmissions[studentId] = {
            studentId,
            studentName: submission.studentName,
            studentEmail: submission.studentEmail,
            attempts: []
          };
        }
        studentSubmissions[studentId].attempts.push(submission);
      });
      
      // Sort attempts for each student (most recent first) and calculate best score
      const finalList = Object.values(studentSubmissions).map(student => {
        student.attempts.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
        student.bestScore = Math.max(...student.attempts.map(a => a.totalScore));
        student.bestPercentage = Math.max(...student.attempts.map(a => a.percentage || 0));
        student.totalAttempts = student.attempts.length;
        return student;
      });
      
      setSubmissions(finalList);
      
    } catch (error) {
      console.error('Error fetching submissions:', error);
      showError('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectExam = (exam) => {
    setSelectedExam(exam);
    setExpandedStudents(new Set());
    fetchExamSubmissions(exam.id);
  };

  const getGrade = (percentage) => {
    if (percentage >= 90) return { grade: 'A+', color: 'text-green-600', bg: 'bg-green-100' };
    if (percentage >= 80) return { grade: 'A', color: 'text-green-600', bg: 'bg-green-100' };
    if (percentage >= 70) return { grade: 'B+', color: 'text-blue-600', bg: 'bg-blue-100' };
    if (percentage >= 60) return { grade: 'B', color: 'text-blue-600', bg: 'bg-blue-100' };
    if (percentage >= 50) return { grade: 'C', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    if (percentage >= 40) return { grade: 'D', color: 'text-orange-600', bg: 'bg-orange-100' };
    return { grade: 'F', color: 'text-red-600', bg: 'bg-red-100' };
  };

  const toggleStudent = (studentId) => {
    setExpandedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  const getSortedExams = () => {
    const sorted = [...exams];
    switch (examSortBy) {
      case 'dateDesc':
        sorted.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
        break;
      case 'dateAsc':
        sorted.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
        break;
      case 'nameAsc':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'submissions':
      default:
        sorted.sort((a, b) => b.submissionCount - a.submissionCount);
        break;
    }
    return sorted;
  };

  const getSortedSubmissions = () => {
    const sorted = [...submissions];
    switch (studentSortBy) {
      case 'dateDesc':
        sorted.sort((a, b) => new Date(b.attempts[0].submittedAt) - new Date(a.attempts[0].submittedAt));
        break;
      case 'dateAsc':
        sorted.sort((a, b) => new Date(a.attempts[0].submittedAt) - new Date(b.attempts[0].submittedAt));
        break;
      case 'nameAsc':
        sorted.sort((a, b) => a.studentName.localeCompare(b.studentName));
        break;
      case 'score':
      default:
        sorted.sort((a, b) => b.bestScore - a.bestScore);
        break;
    }
    return sorted;
  };

  const downloadExamResults = () => {
    if (!selectedExam || submissions.length === 0) return;

    try {
      // Create CSV content
      let csvContent = "Student Name,Email,Total Attempts,Best Score,Total Marks,Percentage,Grade\n";
      
      submissions.forEach(student => {
        const gradeInfo = getGrade(student.bestPercentage);
        const totalMarks = student.attempts[0]?.totalMarks || 0;
        csvContent += `"${student.studentName}","${student.studentEmail}",${student.totalAttempts},${student.bestScore},${totalMarks},${student.bestPercentage.toFixed(2)},${gradeInfo.grade}\n`;
      });

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${selectedExam.name}_results_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showSuccess('Results downloaded successfully!');
    } catch (error) {
      console.error('Error downloading results:', error);
      showError('Failed to download results');
    }
  };

  const downloadStudentDetailedResults = (student) => {
    try {
      // Use the best attempt for detailed results
      const bestAttempt = student.attempts.reduce((prev, current) => 
        (current.totalScore > prev.totalScore) ? current : prev
      );

      const questionResults = bestAttempt.questionResults || {};
      const questionsArray = Object.values(questionResults);

      // Create CSV content
      let csvContent = `Student: ${student.studentName}\n`;
      csvContent += `Email: ${student.studentEmail}\n`;
      csvContent += `Exam: ${selectedExam.name}\n`;
      csvContent += `Total Score: ${bestAttempt.totalScore}/${bestAttempt.totalMarks}\n`;
      csvContent += `Percentage: ${bestAttempt.percentage.toFixed(2)}%\n`;
      csvContent += `Grade: ${getGrade(bestAttempt.percentage).grade}\n\n`;
      
      csvContent += "Question No.,Question,Student Answer,Correct Answer,Status,Marks Scored,Total Marks\n";
      
      questionsArray.forEach((question, index) => {
        const status = question.isCorrect ? 'Correct' : (question.wasAnswered ? 'Wrong' : 'Unanswered');
        csvContent += `${index + 1},"${question.questionText?.replace(/"/g, '""')}","${question.studentAnswer?.replace(/"/g, '""')}","${question.correctAnswer?.replace(/"/g, '""')}",${status},${question.scored},${question.marks}\n`;
      });

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${student.studentName}_${selectedExam.name}_detailed_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showSuccess(`Detailed results for ${student.studentName} downloaded!`);
    } catch (error) {
      console.error('Error downloading detailed results:', error);
      showError('Failed to download detailed results');
    }
  };

  const renderQuestionResults = (attempt) => {
    const questionResults = attempt.questionResults || {};
    const questionsArray = Object.values(questionResults);
    
    return (
      <div className="mt-6 space-y-4">
        <h4 className="text-lg font-bold text-gray-900 mb-4">Question-wise Results</h4>
        {questionsArray.map((question, index) => (
          <div 
            key={index}
            className={`border-2 rounded-lg p-4 ${
              question.isCorrect 
                ? 'border-green-200 bg-green-50' 
                : 'border-red-200 bg-red-50'
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="bg-white px-3 py-1 rounded font-bold text-gray-700">
                  Q{index + 1}
                </span>
                <span className={`px-3 py-1 rounded font-semibold ${
                  question.isCorrect 
                    ? 'bg-green-600 text-white' 
                    : 'bg-red-600 text-white'
                }`}>
                  {question.isCorrect ? '✓ Correct' : '✗ Incorrect'}
                </span>
                <span className={`text-sm font-semibold ${
                  question.scored < 0 ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {question.scored > 0 ? '+' : ''}{question.scored}/{question.marks} marks
                </span>
              </div>
            </div>

            <p className="text-gray-900 font-medium mb-3">{question.questionText}</p>

            <div className="space-y-2">
              <div className="bg-white rounded p-3">
                <p className="text-xs text-gray-600 mb-1">Student's Answer:</p>
                {question.studentAnswerImage ? (
                  <div>
                    <p className={`font-semibold mb-2 ${
                      question.isCorrect ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {question.studentAnswer || 'Not Answered'}
                    </p>
                    <img 
                      src={question.studentAnswerImage} 
                      alt="Student's Answer" 
                      className="max-w-xs rounded-lg shadow-md border-2 border-gray-300"
                    />
                  </div>
                ) : (
                  <p className={`font-semibold ${
                    question.isCorrect ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {question.studentAnswer || 'Not Answered'}
                  </p>
                )}
              </div>

              {!question.isCorrect && question.correctAnswer && (
                <div className="bg-white rounded p-3">
                  <p className="text-xs text-gray-600 mb-1">Correct Answer:</p>
                  {question.correctAnswerImage ? (
                    <div>
                      <p className="font-semibold text-green-700 mb-2">
                        {question.correctAnswer}
                      </p>
                      <img 
                        src={question.correctAnswerImage} 
                        alt="Correct Answer" 
                        className="max-w-xs rounded-lg shadow-md border-2 border-green-300"
                      />
                    </div>
                  ) : (
                    <p className="font-semibold text-green-700">
                      {question.correctAnswer}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading && !selectedExam) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto"></div>
        <p className="text-gray-600 mt-4 font-medium">Loading exams...</p>
      </div>
    );
  }

  if (!selectedExam) {
    // Show list of exams
    return (
      <div>
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Exam Results</h3>
              <p className="text-gray-600">Select an exam to view student results</p>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-semibold text-gray-700">Sort by:</label>
              <select
                value={examSortBy}
                onChange={(e) => setExamSortBy(e.target.value)}
                className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 font-medium"
              >
                <option value="submissions">Most Submissions</option>
                <option value="dateDesc">Newest First</option>
                <option value="dateAsc">Oldest First</option>
                <option value="nameAsc">Name (A-Z)</option>
              </select>
            </div>
          </div>
        </div>

        {exams.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-md">
            <svg className="h-16 w-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p className="text-gray-600 font-medium">No results available</p>
            <p className="text-gray-500 text-sm mt-2">Results will appear once exams are completed</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {getSortedExams().map((exam) => (
              <div
                key={exam.id}
                onClick={() => handleSelectExam(exam)}
                className="bg-white rounded-xl shadow-md p-6 cursor-pointer hover:shadow-lg transition border-2 border-transparent hover:border-indigo-500"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="text-xl font-bold text-gray-900 mb-2">{exam.name}</h4>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        {exam.submissionCount} submissions
                      </span>
                      <span>Total Questions: {exam.totalQuestions}</span>
                      <span>Total Marks: {exam.totalMarks}</span>
                      {exam.startDate && (
                        <span className="flex items-center gap-1">
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {new Date(exam.startDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="bg-indigo-100 text-indigo-600 px-6 py-3 rounded-lg font-bold text-lg">
                    {exam.submissionCount}
                    <div className="text-xs font-normal">Students</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Show student results for selected exam
  return (
    <div>
      {/* Header with back button */}
      <div className="mb-6">
        <button
          onClick={() => {
            setSelectedExam(null);
            setSubmissions([]);
            setExpandedStudents(new Set());
          }}
          className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-semibold mb-4"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Exams
        </button>
        
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">{selectedExam.name}</h3>
            <p className="text-gray-600">
              {submissions.length} students | {selectedExam.totalQuestions} questions | {selectedExam.totalMarks} marks
              {selectedExam.startDate && (
                <span className="ml-2">| Started: {new Date(selectedExam.startDate).toLocaleDateString()}</span>
              )}
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-semibold text-gray-700">Sort students:</label>
              <select
                value={studentSortBy}
                onChange={(e) => setStudentSortBy(e.target.value)}
                className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 font-medium"
              >
                <option value="score">Highest Score</option>
                <option value="dateDesc">Latest Submission</option>
                <option value="dateAsc">Earliest Submission</option>
                <option value="nameAsc">Name (A-Z)</option>
              </select>
            </div>
            
            {submissions.length > 0 && (
              <button
                onClick={downloadExamResults}
                className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download All Results (CSV)
              </button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto"></div>
          <p className="text-gray-600 mt-4 font-medium">Loading results...</p>
        </div>
      ) : submissions.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-md">
          <svg className="h-16 w-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-gray-600 font-medium">No submissions yet</p>
          <p className="text-gray-500 text-sm mt-2">Students haven't taken this exam yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {getSortedSubmissions().map((student, index) => {
            const bestAttempt = student.attempts.reduce((prev, current) => 
              (current.totalScore > prev.totalScore) ? current : prev
            );
            const gradeInfo = getGrade(student.bestPercentage);
            const isExpanded = expandedStudents.has(student.studentId);

            return (
              <div key={student.studentId} className="bg-white rounded-xl shadow-md overflow-hidden">
                {/* Student Header */}
                <div 
                  onClick={() => toggleStudent(student.studentId)}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 cursor-pointer hover:from-indigo-700 hover:to-purple-700 transition"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="bg-white text-indigo-600 font-bold text-lg w-12 h-12 rounded-full flex items-center justify-center">
                        {index + 1}
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-white">{student.studentName}</h4>
                        <p className="text-indigo-100 text-sm">{student.studentEmail}</p>
                        <p className="text-indigo-200 text-xs mt-1">
                          Latest: {new Date(student.attempts[0].submittedAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-2xl font-bold text-white">
                          {student.bestScore}/{bestAttempt.totalMarks}
                        </div>
                        <div className="text-sm text-indigo-100">
                          {student.bestPercentage.toFixed(1)}% - {gradeInfo.grade}
                        </div>
                      </div>
                      
                      <div className="bg-white bg-opacity-20 text-white px-3 py-2 rounded-lg text-sm font-semibold">
                        {student.totalAttempts} {student.totalAttempts === 1 ? 'attempt' : 'attempts'}
                      </div>
                      
                      <svg 
                        className={`h-6 w-6 text-white transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="p-6">
                    {/* Download Button */}
                    <div className="mb-6 flex justify-end">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadStudentDetailedResults(student);
                        }}
                        className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download Details (CSV)
                      </button>
                    </div>

                    {/* All Attempts Summary */}
                    {student.totalAttempts > 1 && (
                      <div className="mb-6 bg-gray-50 rounded-lg p-4">
                        <h5 className="font-bold text-gray-900 mb-3">All Attempts</h5>
                        <div className="space-y-2">
                          {student.attempts.map((attempt, attemptIndex) => (
                            <div key={attempt.id} className="flex items-center justify-between bg-white rounded p-3">
                              <div className="flex items-center gap-3">
                                <span className="font-semibold text-gray-700">
                                  Attempt {student.attempts.length - attemptIndex}
                                </span>
                                <span className="text-sm text-gray-500">
                                  {new Date(attempt.submittedAt).toLocaleString()}
                                </span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="font-bold text-gray-900">
                                  {attempt.totalScore}/{attempt.totalMarks}
                                </span>
                                <span className={`px-3 py-1 rounded font-semibold ${getGrade(attempt.percentage || 0).bg} ${getGrade(attempt.percentage || 0).color}`}>
                                  {(attempt.percentage || 0).toFixed(1)}%
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Best Attempt Details */}
                    <div className="border-t border-gray-200 pt-6">
                      <h5 className="font-bold text-gray-900 mb-4">
                        Best Attempt Details
                        {student.totalAttempts > 1 && (
                          <span className="text-sm font-normal text-gray-600 ml-2">
                            (Score: {bestAttempt.totalScore}/{bestAttempt.totalMarks})
                          </span>
                        )}
                      </h5>
                      
                      {/* Statistics */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        {(() => {
                          const questionResults = bestAttempt.questionResults || {};
                          const questionsArray = Object.values(questionResults);
                          const correctAnswers = questionsArray.filter(q => q.isCorrect).length;
                          const wrongAnswers = questionsArray.filter(q => q.wasAnswered && !q.isCorrect).length;
                          const unanswered = questionsArray.filter(q => !q.wasAnswered).length;

                          return (
                            <>
                              <div className="bg-green-50 rounded-lg p-4 text-center">
                                <div className="text-2xl font-bold text-green-600">{correctAnswers}</div>
                                <div className="text-sm text-gray-600">Correct</div>
                              </div>
                              <div className="bg-red-50 rounded-lg p-4 text-center">
                                <div className="text-2xl font-bold text-red-600">{wrongAnswers}</div>
                                <div className="text-sm text-gray-600">Wrong</div>
                              </div>
                              <div className="bg-yellow-50 rounded-lg p-4 text-center">
                                <div className="text-2xl font-bold text-yellow-600">{unanswered}</div>
                                <div className="text-sm text-gray-600">Unanswered</div>
                              </div>
                              <div className="bg-blue-50 rounded-lg p-4 text-center">
                                <div className="text-2xl font-bold text-blue-600">{bestAttempt.tabSwitchCount || 0}</div>
                                <div className="text-sm text-gray-600">Tab Switches</div>
                              </div>
                            </>
                          );
                        })()}
                      </div>

                      {/* Question-wise Results */}
                      {renderQuestionResults(bestAttempt)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ExamResults;
